import type { ComponentType, ReactNode } from 'react';
import type { ShadcnDashboardWidget } from './types';

export interface PluginLayoutRendererProps {
  page: unknown;
  children: ReactNode;
}

export interface PluginWidgetRendererProps {
  widget: ShadcnDashboardWidget;
  apiBaseUrl: string;
}

type LayoutRenderer = ComponentType<PluginLayoutRendererProps>;
type WidgetRenderer = ComponentType<PluginWidgetRendererProps>;

const layoutRegistry = new Map<string, LayoutRenderer>();
const widgetRegistry = new Map<string, WidgetRenderer>();

export function registerLayout(name: string, renderer: LayoutRenderer): void {
  layoutRegistry.set(name, renderer);
}

export function registerWidget(name: string, renderer: WidgetRenderer): void {
  widgetRegistry.set(name, renderer);
}

export function getRegisteredLayout(name: string): LayoutRenderer | null {
  return layoutRegistry.get(name) ?? null;
}

export function getRegisteredWidget(name: string): WidgetRenderer | null {
  return widgetRegistry.get(name) ?? null;
}

function resolvePluginModuleUrl(plugin: unknown): string | null {
  if (typeof plugin === 'string') {
    return plugin;
  }

  if (!plugin || typeof plugin !== 'object') {
    return null;
  }

  const candidate = plugin as { cdnUrl?: unknown; module?: unknown };
  if (typeof candidate.cdnUrl === 'string') {
    return candidate.cdnUrl;
  }

  if (typeof candidate.module === 'string') {
    return candidate.module;
  }

  return null;
}

export async function loadRuntimePlugins(plugins: unknown[] | undefined): Promise<void> {
  if (!Array.isArray(plugins) || plugins.length === 0) {
    return;
  }

  await Promise.all(
    plugins.map(async entry => {
      const pluginUrl = resolvePluginModuleUrl(entry);
      if (!pluginUrl) {
        return;
      }

      const module = await import(/* @vite-ignore */ pluginUrl) as {
        registerLayout?: (name: string, renderer: LayoutRenderer) => void;
        registerWidget?: (name: string, renderer: WidgetRenderer) => void;
      };

      if (typeof module.registerLayout === 'function') {
        module.registerLayout(registerLayout);
      }

      if (typeof module.registerWidget === 'function') {
        module.registerWidget(registerWidget);
      }
    }),
  );
}
